import { useCallback } from "react";

import { useRegistryApi } from "src/platform/hooks/useRegistryApi";

import type {
  CreateLlmGatewayBindingRequest,
  LlmGatewayBinding,
  LlmGatewayCredentials,
} from "./llmGatewayTypes";

interface PathOptions {
  alias: string;
  bindingId?: string;
}

export function useLlmGatewayApi() {
  const listApi = useRegistryApi<LlmGatewayBinding[]>(
    "application",
    "listOrgServiceBindings",
  );
  const createApi = useRegistryApi<LlmGatewayBinding>(
    "application",
    "createOrgServiceBinding",
  );
  const getApi = useRegistryApi<LlmGatewayBinding>(
    "application",
    "getOrgServiceBinding",
  );
  const credentialsApi = useRegistryApi<LlmGatewayCredentials>(
    "application",
    "getOrgServiceBindingCredentials",
  );
  const rotateApi = useRegistryApi<LlmGatewayBinding>(
    "application",
    "rotateOrgServiceBinding",
  );
  const revokeApi = useRegistryApi<LlmGatewayBinding>(
    "application",
    "revokeOrgServiceBinding",
  );

  const list = useCallback(
    (alias: string) => listApi.call({ pathParams: { alias } }),
    [listApi],
  );
  const create = useCallback(
    (request: CreateLlmGatewayBindingRequest, alias: string) =>
      createApi.call({ pathParams: { alias }, body: request }),
    [createApi],
  );
  const get = useCallback(
    ({ alias, bindingId }: PathOptions) =>
      getApi.call({ pathParams: { alias, bindingId: bindingId ?? "" } }),
    [getApi],
  );
  const getCredentials = useCallback(
    async ({ alias, bindingId }: PathOptions) => {
      try {
        return await credentialsApi.call({
          pathParams: { alias, bindingId: bindingId ?? "" },
        });
      } finally {
        credentialsApi.reset();
      }
    },
    [credentialsApi],
  );
  const rotate = useCallback(
    ({ alias, bindingId }: PathOptions) =>
      rotateApi.call({
        pathParams: { alias, bindingId: bindingId ?? "" },
      }),
    [rotateApi],
  );
  const revoke = useCallback(
    ({ alias, bindingId }: PathOptions) =>
      revokeApi.call({
        pathParams: { alias, bindingId: bindingId ?? "" },
      }),
    [revokeApi],
  );

  return {
    list,
    create,
    get,
    getCredentials,
    rotate,
    revoke,
    loading:
      listApi.loading ||
      createApi.loading ||
      getApi.loading ||
      credentialsApi.loading ||
      rotateApi.loading ||
      revokeApi.loading,
    error:
      listApi.error ||
      createApi.error ||
      getApi.error ||
      credentialsApi.error ||
      rotateApi.error ||
      revokeApi.error,
  };
}
